import { PromptInput, Suggestions } from '@bennows/boows-react';
import { useTranslation } from 'react-i18next';
import { formatDecimal } from '../../lib/format';
import type { AssistantEstimate } from './assistant-api';
import { useAssistant } from './AssistantProvider';
import { useSpeechInput } from './useSpeechInput';
import { VoiceButton, VoiceStatus } from './VoiceButton';

const MAX_LENGTH = 2000;
const COUNTER_FROM = 1800;

interface Props {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** A chip filled the draft: the composer takes the focus. */
  readonly onPick: (text: string) => void;
  readonly onSend: (value: string) => void;
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly chips: readonly string[];
  readonly estimate: AssistantEstimate | undefined;
  /** The credits the last answer cost; null when none were counted. */
  readonly lastCredits: string | null;
}

/** The credit line (spec 110 section 4.15.6): the answer's cost, the estimate before it, free for exempt tenants. */
export function creditLead(t: (key: string, params?: Record<string, unknown>) => string, estimate: AssistantEstimate | undefined, lastCredits: string | null): string | null {
  if (estimate?.exempt) return t('assistant.credits.free');
  if (lastCredits !== null) return t('assistant.credits.answer', { credits: formatDecimal(lastCredits, { maximumFractionDigits: 2 }) });
  if (estimate?.credits_per_turn != null) return t('assistant.credits.estimate', { count: estimate.credits_per_turn });
  return null;
}

/** Suggestion chips, the composer and the credit line. Chips fill the composer; the member sends. */
export function Composer({ value, onChange, onPick, onSend, busy, disabled, chips, estimate, lastCredits }: Props) {
  const { t, i18n } = useTranslation();
  const { open } = useAssistant();
  // The transcript lands in the draft after what is typed; the member checks it and sends.
  const speech = useSpeechInput(i18n.resolvedLanguage ?? 'de', (text) => onChange(`${value}${value && !/\s$/.test(value) ? ' ' : ''}${text}`.slice(0, MAX_LENGTH)), open);
  const lead = creditLead(t, estimate, lastCredits);
  return (
    <div className="ui-assistant__footer">
      {chips.length > 0 && !disabled ? (
        <Suggestions items={chips.slice(0, 3)} ariaLabel={t('assistant.suggestionsLabel')} layout="scroll" onSelect={(chip) => onPick(chip.label)} />
      ) : null}
      {disabled ? <p className="ui-assistant__note">{t('assistant.composer.noPermission')}</p> : null}
      <PromptInput
        value={value}
        onChange={(next) => onChange(next.slice(0, MAX_LENGTH))}
        onSubmit={onSend}
        status={busy ? 'submitting' : 'idle'}
        disabled={disabled}
        maxRows={5}
        toolbar={speech.supported && !disabled ? <VoiceButton state={speech.state} onToggle={speech.toggle} /> : undefined}
        labels={{ input: t('assistant.composer.label'), placeholder: t('assistant.composer.placeholder'), send: t('assistant.composer.send'), hint: t('assistant.composer.hint') }}
      />
      {speech.supported && !disabled ? <VoiceStatus state={speech.state} noticeVisible={speech.noticeVisible} /> : null}
      {value.length >= COUNTER_FROM ? <p className="ui-assistant__note">{t('assistant.composer.counter', { count: value.length, max: MAX_LENGTH })}</p> : null}
      <p className="ui-assistant__credit">{lead ? t('assistant.credits.line', { lead, never: t('assistant.credits.never') }) : t('assistant.credits.never')}</p>
    </div>
  );
}
